/**
 * AI narrowing chat for the marked-times comparison page (PLAN.md "Marked-time comparison &
 * AI narrowing"). Same architecture as trait_chat.ts:
 *
 *   • One OpenAI CONVERSATION per person (server-side history; only the new turn is sent).
 *   • The system instructions are re-sent each turn and carry the CURRENT deterministic
 *     comparison context (chart facts + engine scores from lib/time_compare) — so the model can
 *     only reason over REAL chart differences, and eliminations/assessments never fall out of
 *     context.
 *   • Strict JSON schema (instructions/time_compare_chat.json) with the marked-time keys
 *     injected as enums, so an answer option or assessment can never reference a time that
 *     isn't on the shortlist.
 *
 * Critical boundary (PLAN.md): the AI NEVER ranks. Its `time_assessments` are display-only
 * chips; the deterministic engine scores stay the ranking, and only the USER eliminates a time.
 *
 * Conversation shape (owner spec 2026-07-05): the assistant asks ONE question per turn, carried
 * as prose in `reply`, plus a small set of tappable `options` for that single question — each
 * option annotated with the marked times it points toward. The user taps an option (or types a
 * free-text answer). The assistant then RANKS the shortlist itself: its `rank_suggestions` are
 * AUTO-APPLIED to the running rank (the user no longer promotes/demotes/eliminates by hand).
 */

import { load_instruction, load_schema } from "./instructions";
import {
  call_openai_conversation_json,
  call_openai_conversation_json_streaming,
} from "./openai_service";
import { compare_context_for_ai } from "./time_compare";

/** One tappable answer to the turn's single question; `supports` = the marked-time keys it favours. */
export interface CompareOption {
  label: string;
  supports: string[];
}
export interface CompareAssessment {
  time: string;
  verdict: "supported" | "contradicted" | "neutral";
  reason: string;
}
export interface RankSuggestion {
  time: string;
  action: "promote" | "demote" | "keep";
  reason: string;
}

// Test seam replacing a network call, mirroring trait_chat's set_call_openai_conversation_json_impl.
type ConversationCall = typeof call_openai_conversation_json;
let _call_openai_conversation_json: ConversationCall = call_openai_conversation_json;

export function set_compare_chat_call_impl(impl: ConversationCall | null): void {
  _call_openai_conversation_json = impl ?? call_openai_conversation_json;
}

/** Strict schema with the CURRENT marked-time keys as the only legal time references. */
export function _compare_chat_schema(time_keys: string[]): Record<string, any> {
  return load_schema("time_compare_chat.json", {
    time_keys,
    // `most_likely` may honestly be "too close to call".
    time_keys_nullable: [...time_keys, null],
  });
}

/** System instructions + the per-turn deterministic comparison context block. */
export function _compare_instructions(
  comparison: Record<string, any>,
  eliminated: string[],
  assessments: Record<string, { verdict: string; reason?: string }>,
  user_ranks: Record<string, number> = {},
): string {
  const context = compare_context_for_ai(comparison, eliminated, assessments, user_ranks);
  return (
    load_instruction("time_compare_chat.md") +
    "\n\nCURRENT COMPARISON CONTEXT (deterministic — the only chart facts you may use):\n" +
    JSON.stringify(context)
  );
}

const _VERDICTS = new Set(["supported", "contradicted", "neutral"]);
const _RANK_ACTIONS = new Set(["promote", "demote", "keep"]);

/**
 * One turn of the narrowing conversation. Pass `conversation_id=null` with an empty
 * `user_message` to open the chat (the model introduces itself with its first discriminating
 * question). Returns {reply, options, assessments, rank_suggestions, most_likely, done,
 * conversation_id, error}; `reply` carries the single question, `options` its tappable answers,
 * and every time reference is filtered to the real shortlist keys.
 */
export async function compare_chat_turn(
  conversation_id: string | null,
  user_message: string,
  comparison: Record<string, any>,
  eliminated: string[] = [],
  prior_assessments: Record<string, { verdict: string; reason?: string }> = {},
  user_ranks: Record<string, number> = {},
  run_data: Record<string, any> | null = null,
  log_slug: string | null = null,
  on_reply_delta: ((partial_reply: string) => void) | null = null,
): Promise<Record<string, any>> {
  const time_keys: string[] = (comparison?.times ?? []).map((t: any) => String(t.key));
  if (!time_keys.length) {
    return {
      reply: null,
      options: [],
      assessments: {},
      rank_suggestions: [],
      most_likely: null,
      done: false,
      conversation_id,
      error: "No marked times to compare.",
    };
  }
  const schema = _compare_chat_schema(time_keys);
  const instructions = _compare_instructions(comparison, eliminated, prior_assessments, user_ranks);
  const user_input =
    user_message && user_message.trim()
      ? user_message.trim()
      : "Start the narrowing. Briefly say what genuinely differs between my marked times, then ask your first single question.";

  let parsed: Record<string, any> | null;
  let error: string | null;
  if (on_reply_delta) {
    [parsed, conversation_id, error] = await call_openai_conversation_json_streaming(
      "time_compare_chat",
      schema,
      instructions,
      conversation_id,
      user_input,
      on_reply_delta,
      log_slug,
      run_data,
    );
  } else {
    [parsed, conversation_id, error] = await _call_openai_conversation_json(
      "time_compare_chat",
      schema,
      instructions,
      conversation_id,
      user_input,
      log_slug,
      run_data,
    );
  }
  if (error || !parsed) {
    return {
      reply: null,
      options: [],
      assessments: {},
      rank_suggestions: [],
      most_likely: null,
      done: false,
      conversation_id,
      error: error || "No response from the assistant.",
    };
  }

  const keySet = new Set(time_keys);
  // The turn's single question carries tappable answer options; each option's `supports` is
  // filtered to real shortlist keys (shown to the user in brackets).
  const options: CompareOption[] = [];
  for (const o of parsed["options"] ?? []) {
    if (!o || typeof o.label !== "string") continue;
    options.push({
      label: o.label,
      supports: (Array.isArray(o.supports) ? o.supports : []).filter((k: any) => keySet.has(k)),
    });
  }

  const assessments: Record<string, { verdict: string; reason: string }> = {};
  for (const a of parsed["time_assessments"] ?? []) {
    if (!a || !keySet.has(a.time) || !_VERDICTS.has(a.verdict)) continue;
    assessments[a.time] = { verdict: a.verdict, reason: String(a.reason ?? "") };
  }

  // Promote/demote ADVICE — the user applies it (one tap per step); the AI never edits a rank.
  const rank_suggestions: RankSuggestion[] = [];
  for (const s of parsed["rank_suggestions"] ?? []) {
    if (!s || !keySet.has(s.time) || !_RANK_ACTIONS.has(s.action)) continue;
    rank_suggestions.push({ time: s.time, action: s.action, reason: String(s.reason ?? "") });
  }

  const most_likely = keySet.has(parsed["most_likely"]) ? parsed["most_likely"] : null;

  return {
    reply: String(parsed["reply"] ?? "").trim(),
    options,
    assessments,
    rank_suggestions,
    most_likely,
    done: Boolean(parsed["done"]),
    conversation_id,
    error: null,
  };
}
